import { apiRequest } from "./apiClient";

export type CardSummary = {
  id: number;
  cardNumber: string;
  referenceId?: string | null;
  statusId?: number | null;
  statusName?: string | null;
  issueDate?: string | null;
  expireDate?: string | null;
  accountId?: number | null;
  accountNo?: string | null;
  accountName?: string | null;
  walletBalance?: number | null;
};

export type CardStatusLookupDto = {
  id: number;
  name: string;
};

export type CardDetail = CardSummary & {
  lookups?: {
    statuses?: CardStatusLookupDto[];
  };
};

export type CardSavePayload = {
  cardNumber: string;
  statusId: number;
  accountId?: number | null;
  referenceId?: string | null;
  issueDate?: string | null;
  expireDate?: string | null;
};

export type PaginationMeta = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

export type PaginatedResponse<T> = {
  items: T[];
  meta: PaginationMeta;
};

export type FetchCardsOptions = {
  page?: number;
  pageSize?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  signal?: AbortSignal;
};

const buildCardsQuery = (options?: FetchCardsOptions) => {
  if (!options) {
    return "";
  }
  const params = new URLSearchParams();
  if (options.page && options.page > 0) {
    params.append("page", String(options.page));
  }
  if (options.pageSize && options.pageSize > 0) {
    params.append("pageSize", String(options.pageSize));
  }
  if (options.search?.trim()) {
    params.append("search", options.search.trim());
  }
  if (options.fromDate) {
    params.append("fromDate", options.fromDate);
  }
  if (options.toDate) {
    params.append("toDate", options.toDate);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};

export function fetchCards(options?: FetchCardsOptions) {
  const query = buildCardsQuery(options);
  return apiRequest<PaginatedResponse<CardSummary>>(`/api/cards${query}`, {
    signal: options?.signal,
  });
}

type CardDetailOptions = {
  includeLookups?: boolean;
};

export function fetchCardDetail(
  cardId: number | string,
  signal?: AbortSignal,
  options?: CardDetailOptions,
) {
  const params = new URLSearchParams();
  if (options?.includeLookups) {
    params.append("includeLookups", "true");
  }
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  return apiRequest<CardDetail>(`/api/cards/${cardId}${suffix}`, { signal });
}

export type CardLookupsDto = CardDetail["lookups"];

export function fetchCardLookups(signal?: AbortSignal) {
  return apiRequest<CardLookupsDto>("/api/cards/lookups", { signal });
}

export function createCard(payload: CardSavePayload) {
  return apiRequest<CardDetail>(`/api/cards`, {
    method: "POST",
    body: payload,
  });
}

export function updateCard(cardId: number | string, payload: CardSavePayload) {
  return apiRequest<CardDetail>(`/api/cards/${cardId}`, {
    method: "PUT",
    body: payload,
  });
}

export function deleteCard(cardId: number | string) {
  return apiRequest<void>(`/api/cards/${cardId}`, {
    method: "DELETE",
  });
}

export type CardImportError = {
  rowNumber: number;
  cardNumber?: string | null;
  referenceId?: string | null;
  message: string;
};

export type CardImportResponse = {
  created: number;
  skipped: number;
  errors: CardImportError[];
};

export function importCardsFile(file: File | Blob) {
  const formData = new FormData();
  formData.append("file", file);
  return apiRequest<CardImportResponse>(`/api/cards/import`, {
    method: "POST",
    body: formData,
  });
}
